import { useQuery } from "@tanstack/react-query";
import { unstable_noStore } from "next/cache";
import { useCallback, useEffect, useRef, useState } from "react";
import { formatEther } from "viem";
import { fetchJSON, getJSON } from "@/utils/fetcher";

export interface VaultDeposit {
  amount: string;
  timestamp: string;
  user: { id: string };
  round: { strike: string };
  beraname: string | null;
  profile_picture: string | null;
}
export interface FormattedDeposit {
  amount: string;
  strike: string;
  address: string;
  timestamp: number;
  beraname: string | null;
  profile_picture: string | null;
}

export function useMarqueeDeposits() {
  unstable_noStore();
  const allDepositsRef = useRef<Record<string, FormattedDeposit>>({});
  const [initialDeposits, setInitialDeposits] = useState<FormattedDeposit[]>([]);
  const [isInitialized, setIsInitialized] = useState(false);
  const addDeposits = useCallback((newDeposits: VaultDeposit[]) => {
    if (!newDeposits || newDeposits.length === 0) return;
    const formattedNewDeposits = newDeposits.reduce(
      (acc, deposit) => {
        const key = `${deposit.user.id}-${deposit.timestamp}`;
        if (allDepositsRef.current[key]) return acc;
        acc[key] = {
          amount: formatEther(BigInt(deposit.amount)),
          strike: deposit.round.strike,
          address: deposit.user.id,
          timestamp: Number(deposit.timestamp),
          beraname: deposit.beraname,
          profile_picture: deposit.profile_picture,
        };
        return acc;
      },
      {} as Record<string, FormattedDeposit>,
    );
    allDepositsRef.current = { ...allDepositsRef.current, ...formattedNewDeposits };
  }, []);

  const { data, error, isLoading } = useQuery({
    queryKey: ["vault-deposits", "initial"],
    queryFn: () => getJSON<VaultDeposit[]>("/api/earn/vault-deposits"),
    staleTime: 3000,
    refetchInterval: 0,
    gcTime: 5 * 60 * 1000,
    select: (d) => d,
  });

  useEffect(() => {
    if (!isInitialized && data && data.length > 0) {
      addDeposits(data);
      const sortedDeposits = Object.values(allDepositsRef.current).sort(
        (a, b) => b.timestamp - a.timestamp,
      );
      setInitialDeposits(sortedDeposits);
      setIsInitialized(true);
    }
  }, [data, isInitialized, addDeposits]);

  useEffect(() => {
    if (!isInitialized) return;
    const pollInterval = setInterval(async () => {
      try {
        const newData = await fetchJSON<VaultDeposit[]>("/api/earn/vault-deposits");
        if (newData) {
          addDeposits(newData);
        }
      } catch (_error) {}
    }, 5000);
    return () => clearInterval(pollInterval);
  }, [isInitialized, addDeposits]);

  return { deposits: initialDeposits, loading: isLoading && !isInitialized, error };
}

export function useMarqueeItems(initialItems: FormattedDeposit[]) {
  const marqueeItemsRef = useRef<FormattedDeposit[]>([]);
  const isInitializedRef = useRef(false);
  useEffect(() => {
    if (initialItems.length > 0 && !isInitializedRef.current) {
      const repeatCount = Math.max(1, Math.ceil(50 / initialItems.length));
      const repeatedItems = Array(repeatCount)
        .fill(null)
        .flatMap(() => [...initialItems]);
      marqueeItemsRef.current = repeatedItems;
      isInitializedRef.current = true;
    }
  }, [initialItems]);
  useEffect(() => {
    if (!isInitializedRef.current || marqueeItemsRef.current.length === 0) return;
    const pollInterval = setInterval(async () => {
      try {
        const newData = await fetchJSON<VaultDeposit[]>("/api/earn/vault-deposits");
        if (!newData || newData.length === 0) return;
        const existingKeys = new Set(
          marqueeItemsRef.current.map((d) => `${d.address}-${d.timestamp}`),
        );
        const newDeposits = newData
          .filter((deposit) => !existingKeys.has(`${deposit.user.id}-${deposit.timestamp}`))
          .map((deposit) => ({
            amount: formatEther(BigInt(deposit.amount)),
            strike: deposit.round.strike,
            address: deposit.user.id,
            timestamp: Number(deposit.timestamp),
            beraname: deposit.beraname,
            profile_picture: deposit.profile_picture,
          }));
        if (newDeposits.length > 0) {
          newDeposits.sort((a, b) => b.timestamp - a.timestamp);
          const currentLength = marqueeItemsRef.current.length;
          newDeposits.forEach((deposit, index) => {
            const position = Math.floor(currentLength / 10) * (index + 1);
            const safePosition = Math.min(position, marqueeItemsRef.current.length);
            marqueeItemsRef.current.splice(safePosition, 0, deposit);
          });
          if (marqueeItemsRef.current.length > 100) {
            const excess = marqueeItemsRef.current.length - 100;
            for (let i = 0; i < excess; i++) {
              const startPos = Math.floor(marqueeItemsRef.current.length / 3);
              const endPos = Math.floor((marqueeItemsRef.current.length * 2) / 3);
              const removePos = startPos + Math.floor(Math.random() * (endPos - startPos));
              marqueeItemsRef.current.splice(removePos, 1);
            }
          }
        }
      } catch (_error) {}
    }, 5000);
    return () => clearInterval(pollInterval);
  }, []);
  return marqueeItemsRef;
}
